import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { ModalBackdrop } from "@mui/joy/Modal/Modal";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { collectionData } from "rxfire/firestore";
import { auth, db, onAuthStateChanged, signOut } from "../config/firebase";
import { fetchPrices, getCachedPrices } from "../config/priceCache";
import { getNotification, getUserInfo } from "../config/services";
import {
  notification$,
  notificationall$,
  userinfo$,
  walletData$,
  walletInfos$,
} from "../redux/action";

const PRICE_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Build the wallet coin array from balances + addresses + cached prices.
 * Pure — no network calls.
 */
function buildWalletArray(balances, addresses, prices) {
  const {
    btc_balance,
    bnb_balance,
    tron_balance,
    sol_balance,
    avax_balance,
    arb_balance,
    eth_balance,
    usdt_balance,
    usdterc20_balance,
    usdtpoly_balance,
    usdtsol_balance,
    usdtavax_balance,
    usdtarb_balance,
    usdtbnb_balance,
  } = balances;

  const { btc = "", bnb = "", eth = "", trx = "" } = addresses || {};
  const p = prices || {};
  const price = (ticker) => p[ticker]?.usd ?? 0;
  const change = (ticker) => p[ticker]?.usd_24h_change ?? 0;
  const toCoins = (usd, ticker) => {
    const px = price(ticker);
    if (ticker === "USDT") return usd;
    return px > 0 ? usd / px : 0;
  };

  const array = [
    {
      coinname: "BITCOIN",
      cointype: "btc_balance",
      price: price("BTC"),
      difference: change("BTC"),
      balance: btc_balance,
      balancecoin: toCoins(btc_balance, "BTC"),
      code: "BTC",
      codeswap: "BTC",
      image: "./images/coins/btc.png",
      address: btc,
      network: "BTC",
      thumbimage: "./images/coins/btc.png",
      contractaddress: false,
    },
    {
      coinname: "ETHERUEM",
      cointype: "eth_balance",
      price: price("ETH"),
      difference: change("ETH"),
      balance: eth_balance,
      balancecoin: toCoins(eth_balance, "ETH"),
      code: "ETH",
      codeswap: "ETH",
      image: "./images/coins/eth.png",
      address: eth,
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: false,
    },
    {
      coinname: "BNB SMART",
      cointype: "bnb_balance",
      price: price("BNB"),
      difference: change("BNB"),
      balance: bnb_balance,
      balancecoin: toCoins(bnb_balance, "BNB"),
      code: "BNB",
      codeswap: "BNB",
      image: "./images/coins/bnb.png",
      address: bnb,
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: false,
    },
    {
      coinname: "SOLANA",
      cointype: "sol_balance",
      price: price("SOL"),
      difference: change("SOL"),
      balance: sol_balance,
      balancecoin: toCoins(sol_balance, "SOL"),
      code: "SOL",
      codeswap: "SOL",
      image: "./images/coins/sol.png",
      address: eth,
      network: "Solana",
      thumbimage: "./images/coins/sol.png",
      contractaddress: false,
    },
    {
      coinname: "ARBITRUM",
      cointype: "arb_balance",
      price: price("ARB"),
      difference: change("ARB"),
      balance: arb_balance,
      balancecoin: toCoins(arb_balance, "ARB"),
      code: "ARB",
      codeswap: "ARB",
      image: "./images/coins/arb.png",
      address: eth,
      network: "Arbitrum",
      thumbimage: "./images/coins/arb.png",
      contractaddress: false,
    },
    {
      coinname: "AVALANCHE",
      cointype: "avax_balance",
      price: price("AVAX"),
      difference: change("AVAX"),
      balance: avax_balance,
      balancecoin: toCoins(avax_balance, "AVAX"),
      code: "AVAX",
      codeswap: "AVAX",
      image: "./images/coins/avax.png",
      address: eth,
      network: "Avax",
      thumbimage: "./images/coins/avax.png",
      contractaddress: false,
    },
    {
      coinname: "TRON",
      cointype: "tron_balance",
      price: price("TRX"),
      difference: change("TRX"),
      balance: tron_balance,
      balancecoin: toCoins(tron_balance, "TRX"),
      code: "TRX",
      codeswap: "TRX",
      image: "./images/coins/tron.png",
      address: trx,
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: false,
    },
    {
      coinname: "USDT",
      cointype: "usdt_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdt_balance,
      balancecoin: toCoins(usdt_balance, "USDT"),
      code: "USDT TRC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: trx,
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdterc20_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdterc20_balance,
      balancecoin: toCoins(usdterc20_balance, "USDT"),
      code: "USDT ERC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: eth,
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtbnb_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtbnb_balance,
      balancecoin: toCoins(usdtbnb_balance, "USDT"),
      code: "USDT BSC",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: bnb,
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtpoly_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtpoly_balance,
      balancecoin: toCoins(usdtpoly_balance, "USDT"),
      code: "USDT POLY",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: eth,
      network: "Polygon",
      thumbimage: "./images/coins/pol.png",
      contractaddress: true,
      networkprice: price("POLY"),
    },
    {
      coinname: "USDT",
      cointype: "usdtsol_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtsol_balance,
      balancecoin: toCoins(usdtsol_balance, "USDT"),
      code: "USDT SOL",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: eth,
      network: "Solana",
      thumbimage: "./images/coins/sol.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtarb_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtarb_balance,
      balancecoin: toCoins(usdtarb_balance, "USDT"),
      code: "USDT ARB",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: eth,
      network: "Arbitrum",
      thumbimage: "./images/coins/arb.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtavax_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtavax_balance,
      balancecoin: toCoins(usdtavax_balance, "USDT"),
      code: "USDT AVAX",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: eth,
      network: "Avax",
      thumbimage: "./images/coins/avax.png",
      contractaddress: true,
    },
  ];

  array.sort((a, b) => b.balance - a.balance);
  return array;
}

export function DashboardIndex() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);

  const latestBalancesRef = React.useRef(null);
  const latestAddressesRef = React.useRef(null);

  const rebuildWallet = React.useCallback(
    (balances, addresses, prices) => {
      if (!balances) return;
      dispatch(
        walletData$(
          buildWalletArray(balances, addresses || latestAddressesRef.current, prices)
        )
      );
    },
    [dispatch]
  );

  React.useEffect(() => {
    let userSub = null;
    let notifSub = null;
    let cancelled = false;

    // 1. Prices: independent of Firestore — TTL cache + interval refresh
    fetchPrices().then(({ prices }) => {
      if (cancelled) return;
      if (latestBalancesRef.current) {
        rebuildWallet(latestBalancesRef.current, latestAddressesRef.current, prices);
      }
    });

    const priceInterval = setInterval(async () => {
      const { prices } = await fetchPrices();
      if (cancelled || !latestBalancesRef.current) return;
      rebuildWallet(latestBalancesRef.current, latestAddressesRef.current, prices);
    }, PRICE_REFRESH_MS);

    // 2. Auth + Firestore — never calls CoinGecko
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (userSub) {
        userSub.unsubscribe();
        userSub = null;
      }
      if (notifSub) {
        notifSub.unsubscribe();
        notifSub = null;
      }

      if (!user) {
        navigate("/auth/login");
        return;
      }

      getNotification(user.uid);

      userSub = getUserInfo(user.uid).subscribe((userData) => {
        const {
          btc_balance,
          bnb_balance,
          tron_balance,
          poly_balance,
          sol_balance,
          avax_balance,
          arb_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          usdtpoly_balance,
          usdtsol_balance,
          usdtavax_balance,
          usdtarb_balance,
          usdtbnb_balance,
          addressindex,
          suspended,
        } = userData;

        if (suspended === true) {
          signOut(auth)
            .then(() => navigate("/auth/login"))
            .catch(console.error);
          return;
        }

        const totalBalance = [
          btc_balance,
          bnb_balance,
          tron_balance,
          poly_balance,
          sol_balance,
          avax_balance,
          arb_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          usdtpoly_balance,
          usdtsol_balance,
          usdtavax_balance,
          usdtarb_balance,
          usdtbnb_balance,
        ].reduce((acc, cur) => acc + (cur || 0), 0);

        dispatch(userinfo$({ ...userData, id: user.uid, totalBalance }));

        const balances = {
          btc_balance,
          bnb_balance,
          tron_balance,
          sol_balance,
          avax_balance,
          arb_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          usdtpoly_balance,
          usdtsol_balance,
          usdtavax_balance,
          usdtarb_balance,
          usdtbnb_balance,
        };
        latestBalancesRef.current = balances;

        // Wallet addresses once per user update; rebuild with cached prices only
        getDocs(collection(db, "walletaddresses")).then((querySnapshot) => {
          if (cancelled || !querySnapshot.docs[addressindex]) return;
          const addrData = querySnapshot.docs[addressindex].data();
          latestAddressesRef.current = addrData;
          dispatch(walletInfos$(addrData));
          rebuildWallet(balances, addrData, getCachedPrices());
          setLoading(false);
        });
      });

      const txRef = query(
        collection(db, "users", user.uid, "transactions"),
        orderBy("timestamp", "desc")
      );
      notifSub = collectionData(txRef).subscribe((data) => {
        const unseen = data.filter((docItem) => !("seen" in docItem)).length;
        dispatch(notificationall$(data.length));
        dispatch(notification$(unseen));
      });
    });

    return () => {
      cancelled = true;
      clearInterval(priceInterval);
      unsubscribeAuth();
      if (userSub) userSub.unsubscribe();
      if (notifSub) notifSub.unsubscribe();
    };
  }, [dispatch, navigate, rebuildWallet]);

  return (
    <>
      <Helmet>
        <script src="//code.jivosite.com/widget/2egT4UMWJz" async></script>
      </Helmet>
      {loading ? (
        <ModalBackdrop
          sx={{
            flexDirection: "column",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }}
        >
          <Box
            mt={1}
            display={"flex"}
            justifyContent={"center"}
            position={"relative"}
            top={"40%"}
          >
            <Stack spacing={1} alignItems={"center "}>
              <CircularProgress color="primary" />
              <Typography level="body-lg">Loading...</Typography>
            </Stack>
          </Box>
        </ModalBackdrop>
      ) : (
        <Outlet />
      )}
    </>
  );
}
